import { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdSave } from 'react-icons/md';
import { addPricing, calculatePricingStats, subscribeToPricing, updatePricing, subscribeToPricingSettings, updatePricingSettings, seedMissingSubjects } from '../../services/pricingService';
import Modal from '../../components/common/Modal';

const Pricing = () => {
  const [editMode, setEditMode] = useState({});
  const [prices, setPrices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalSubjects: 0,
    averages: { monthly: 0, termly: 0, annual: 0 },
  });
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSubject, setNewSubject] = useState({
    subject: '',
    monthly: 1000,
    termly: 3000,
    annual: 10000,
    active: true,
  });

  const [settings, setSettings] = useState({
    singleSubjectBase: { price: 1000, active: true },
    threeSubjects: { price: 2500, discount: 0, active: true },
    sixSubjects: { price: 4000, discount: 0, active: true },
    utmeBundle: { price: 3500, discount: 0, active: true },
    freePreviews: { lessons: 3, quizzes: 1 },
    termPricing: {
      singleSubject: { price: 2500, active: true },
      threeSubjects: { price: 6500, active: true },
      sixSubjects: { price: 10000, active: true },
    }
  });
  const [editSettingsMode, setEditSettingsMode] = useState(false);
  const [tempSettings, setTempSettings] = useState(null);

  // Subscribe to pricing updates
  useEffect(() => {
    setLoading(true);
    const unsubscribePricing = subscribeToPricing((data) => {
      setPrices(data);
      setStats(calculatePricingStats(data));
      setLoading(false);
    });

    const unsubscribeSettings = subscribeToPricingSettings((data) => {
      setSettings(data);
    });

    return () => {
      unsubscribePricing();
      unsubscribeSettings();
    };
  }, []);

  const handleSaveSettings = async () => {
    try {
      await updatePricingSettings(tempSettings);
      setEditSettingsMode(false);
      alert('Global Pricing Settings saved successfully!');
    } catch (error) {
      alert('Failed to save settings');
    }
  };

  const handleEditPrice = (priceId) => {
    setEditMode({ ...editMode, [priceId]: true });
  };

  const handleSavePrice = async (priceId) => {
    const priceItem = prices.find(p => p.id === priceId);
    if (!priceItem) return;

    try {
      await updatePricing(priceId, {
        monthly: priceItem.monthly,
        termly: priceItem.termly,
        annual: priceItem.annual,
        active: priceItem.active,
      });
      setEditMode({ ...editMode, [priceId]: false });
      alert('Price updated successfully!');
    } catch (error) {
      console.error('Error updating price:', error);
      alert('Failed to update price');
    }
  };

  const handlePriceChange = (priceId, field, value) => {
    setPrices(prices.map(p => 
      p.id === priceId ? { ...p, [field]: parseInt(value) || 0 } : p
    ));
  };

  const toggleSubjectStatus = async (priceId) => {
    const priceItem = prices.find(p => p.id === priceId);
    if (!priceItem) return;

    try {
      await updatePricing(priceId, {
        active: !priceItem.active,
      });
    } catch (error) {
      console.error('Error toggling status:', error);
      alert('Failed to update status');
    }
  };

  const handleAddSubject = async () => {
    if (!newSubject.subject.trim()) {
      alert('Please enter a subject name');
      return;
    }

    try {
      await addPricing(newSubject);
      setShowAddModal(false);
      setNewSubject({
        subject: '',
        monthly: 1000,
        termly: 3000,
        annual: 10000,
        active: true,
      });
      alert('Subject added successfully!');
    } catch (error) {
      console.error('Error adding subject:', error);
      alert('Failed to add subject');
    }
  };

  const handleSeedSubjects = async () => {
    try {
      setLoading(true);
      await seedMissingSubjects(prices);
      alert('Missing subjects added and prices updated to 1000!');
      setLoading(false);
    } catch (error) {
      alert('Failed to seed subjects');
      setLoading(false);
    }
  };

  return (
    <div className="w-full">
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-darkNavy mb-2 font-poppins">Pricing Management</h1>
        <p className="text-gray-500">Control subject and bundle pricing</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-6 md:mb-8">
        <div className="bg-white p-3 md:p-4 rounded-xl border border-gray-200">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Active Subjects</p>
          <p className="text-xl md:text-2xl font-bold text-darkNavy">
            {prices.filter(p => p.active).length}
          </p>
        </div>
        <div className="bg-white p-3 md:p-4 rounded-xl border border-gray-200">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Avg. Monthly Price</p>
          <p className="text-xl md:text-2xl font-bold text-darkNavy">₦{stats.averages.monthly.toLocaleString()}</p>
        </div>
        <div className="bg-white p-3 md:p-4 rounded-xl border border-gray-200">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Bundle Discount</p>
          <p className="text-xl md:text-2xl font-bold text-green-600">17-25%</p>
        </div>
        <div className="bg-white p-3 md:p-4 rounded-xl border border-gray-200">
          <p className="text-xs md:text-sm text-gray-500 mb-1">Total Subjects</p>
          <p className="text-xl md:text-2xl font-bold text-darkNavy">{stats.totalSubjects}</p>
        </div>
      </div>

      <div className="flex flex-col gap-6">
        {/* Individual Subject Pricing */}
        <div className="bg-white rounded-2xl border border-gray-200 p-3 md:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <h2 className="text-xl font-bold text-darkNavy">Individual Subject Pricing</h2>
            <div className="flex gap-2 w-full sm:w-auto">
              <button 
                onClick={handleSeedSubjects}
                className="flex-1 sm:flex-none flex justify-center items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors">
                Set All to ₦1000
              </button>
              <button 
                onClick={() => setShowAddModal(true)}
                className="flex-1 sm:flex-none flex justify-center items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition-colors">
                <MdAdd size={18} /> Add
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {loading ? (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-400">Loading pricing...</p>
              </div>
            ) : prices.length === 0 ? (
              <div className="col-span-full text-center py-8">
                <p className="text-gray-400">No pricing data available</p>
              </div>
            ) : (
              prices.map((priceItem) => (
                <div key={priceItem.id} className="flex items-center justify-between py-2 px-3 sm:p-4 bg-gray-50 rounded-xl gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 capitalize text-sm sm:text-base truncate">{priceItem.subject}</p>
                    <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                      {editMode[priceItem.id] ? (
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-600 text-xs sm:text-sm">₦</span>
                          <input
                            type="number"
                            value={priceItem.monthly}
                            onChange={(e) => handlePriceChange(priceItem.id, 'monthly', e.target.value)}
                            className="w-16 sm:w-24 px-1.5 py-0.5 sm:px-2 sm:py-1 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 text-xs sm:text-sm"
                          />
                          <span className="text-[10px] sm:text-sm text-gray-500">/mo</span>
                        </div>
                      ) : (
                        <p className="text-xs sm:text-sm text-gray-600">
                          ₦{priceItem.monthly?.toLocaleString()}/month
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                    <span className={`px-2 py-0.5 sm:px-3 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold ${
                      priceItem.active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {priceItem.active ? 'Active' : 'Inactive'}
                    </span>
                    
                    {editMode[priceItem.id] ? (
                      <button
                        onClick={() => handleSavePrice(priceItem.id)}
                        className="p-1.5 sm:p-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
                        title="Save"
                      >
                        <MdSave size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </button>
                    ) : (
                      <button
                        onClick={() => handleEditPrice(priceItem.id)}
                        className="p-1.5 sm:p-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                        title="Edit"
                      >
                        <MdEdit size={16} className="sm:w-[18px] sm:h-[18px]" />
                      </button>
                    )}
                    
                    <button
                      onClick={() => toggleSubjectStatus(priceItem.id)}
                      className="p-1.5 sm:p-2 bg-amber-100 text-amber-700 rounded-lg hover:bg-amber-200 transition-colors text-[10px] sm:text-xs font-semibold px-2 sm:px-3"
                      title={priceItem.active ? 'Deactivate' : 'Activate'}
                    >
                      {priceItem.active ? 'Disable' : 'Enable'}
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="flex justify-center sm:justify-end">
          {!editSettingsMode ? (
            <button 
              onClick={() => {
                setTempSettings(settings);
                setEditSettingsMode(true);
              }}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-2.5 bg-blue-100 text-blue-700 rounded-lg font-semibold hover:bg-blue-200 transition-colors"
            >
              <MdEdit size={20} /> Edit Bundle & Global Settings
            </button>
          ) : (
            <div className="flex gap-3 w-full sm:w-auto justify-center sm:justify-end">
              <button 
                onClick={() => setEditSettingsMode(false)}
                className="flex-1 sm:flex-none px-6 py-2.5 bg-gray-100 text-gray-700 rounded-lg font-semibold hover:bg-gray-200 transition-colors text-center"
              >
                Cancel
              </button>
              <button 
                onClick={handleSaveSettings}
                className="flex-1 sm:flex-none flex items-center justify-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors"
              >
                <MdSave size={20} /> Save Settings
              </button>
            </div>
          )}
        </div>

        {/* Bundle Pricing Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 3-Subject Bundle */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-bold text-darkNavy mb-4">Multi-Subject Bundle (3 Subjects)</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Bundle Discount (%)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-20 px-2 py-1 border rounded" value={tempSettings.threeSubjects.discount} onChange={e => setTempSettings({...tempSettings, threeSubjects: {...tempSettings.threeSubjects, discount: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="text-green-600 font-semibold">{settings.threeSubjects.discount}%</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-gray-900 font-bold text-lg">Bundle Price</span>
                {editSettingsMode ? (
                  <div className="flex items-center gap-1">
                    <span className="font-bold">₦</span>
                    <input type="number" className="w-24 px-2 py-1 border rounded font-bold" value={tempSettings.threeSubjects.price} onChange={e => setTempSettings({...tempSettings, threeSubjects: {...tempSettings.threeSubjects, price: parseInt(e.target.value) || 0}})} />
                  </div>
                ) : (
                  <span className="text-primary font-bold text-2xl">
                    ₦{settings.threeSubjects.price.toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => editSettingsMode ? setTempSettings({...tempSettings, threeSubjects: {...tempSettings.threeSubjects, active: !tempSettings.threeSubjects.active}}) : null}
                  className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                  (editSettingsMode ? tempSettings.threeSubjects.active : settings.threeSubjects.active)
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}>
                  {(editSettingsMode ? tempSettings.threeSubjects.active : settings.threeSubjects.active) ? 'Disable Bundle' : 'Enable Bundle'}
                </button>
              </div>
            </div>
          </div>

          {/* 5-Subject Package Bundle */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-bold text-darkNavy mb-4">Multi-Subject Bundle (5 Subjects)</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Bundle Discount (%)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-20 px-2 py-1 border rounded text-black" value={tempSettings?.sixSubjects?.discount || 0} onChange={e => setTempSettings({...tempSettings, sixSubjects: {...tempSettings.sixSubjects, discount: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="text-green-600 font-semibold">{settings?.sixSubjects?.discount || 0}%</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-gray-900 font-bold text-lg">Bundle Price</span>
                {editSettingsMode ? (
                  <div className="flex items-center gap-1">
                    <span className="font-bold">₦</span>
                    <input type="number" className="w-24 px-2 py-1 border rounded text-black font-bold" value={tempSettings?.sixSubjects?.price || 0} onChange={e => setTempSettings({...tempSettings, sixSubjects: {...tempSettings.sixSubjects, price: parseInt(e.target.value) || 0}})} />
                  </div>
                ) : (
                  <span className="text-primary font-bold text-2xl">
                    ₦{(settings?.sixSubjects?.price || 4000).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => editSettingsMode ? setTempSettings({...tempSettings, sixSubjects: {...tempSettings.sixSubjects, active: !tempSettings.sixSubjects.active}}) : null}
                  className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                    (editSettingsMode ? tempSettings?.sixSubjects?.active : settings?.sixSubjects?.active)
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  {(editSettingsMode ? tempSettings?.sixSubjects?.active : settings?.sixSubjects?.active) ? 'Disable Package' : 'Enable Package'}
                </button>
              </div>
            </div>
          </div>

          {/* UTME 4-Subject Bundle */}
          <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-bold text-darkNavy mb-4">UTME Bundle (4 Subjects)</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Bundle Discount (%)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-20 px-2 py-1 border rounded text-black" value={tempSettings?.utmeBundle?.discount || 0} onChange={e => setTempSettings({...tempSettings, utmeBundle: {...tempSettings.utmeBundle, discount: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="text-green-600 font-semibold">{settings?.utmeBundle?.discount || 0}%</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-4 border-t border-gray-200">
                <span className="text-gray-900 font-bold text-lg">Bundle Price</span>
                {editSettingsMode ? (
                  <div className="flex items-center gap-1">
                    <span className="font-bold">₦</span>
                    <input type="number" className="w-24 px-2 py-1 border rounded text-black font-bold" value={tempSettings?.utmeBundle?.price || 0} onChange={e => setTempSettings({...tempSettings, utmeBundle: {...tempSettings.utmeBundle, price: parseInt(e.target.value) || 0}})} />
                  </div>
                ) : (
                  <span className="text-primary font-bold text-2xl">
                    ₦{(settings?.utmeBundle?.price || 3500).toLocaleString()}
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button 
                  onClick={() => editSettingsMode ? setTempSettings({...tempSettings, utmeBundle: {...tempSettings.utmeBundle, active: !tempSettings.utmeBundle?.active}}) : null}
                  className={`flex-1 py-2 rounded-lg font-semibold transition-colors ${
                  (editSettingsMode ? tempSettings?.utmeBundle?.active : settings?.utmeBundle?.active)
                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                    : 'bg-green-100 text-green-700 hover:bg-green-200'
                }`}>
                  {(editSettingsMode ? tempSettings?.utmeBundle?.active : settings?.utmeBundle?.active) ? 'Disable Bundle' : 'Enable Bundle'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Free Preview Settings & Base Price */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-bold text-darkNavy mb-4">Global Default Settings</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Single Subject Base Price (₦)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.singleSubjectBase.price} onChange={e => setTempSettings({...tempSettings, singleSubjectBase: {...tempSettings.singleSubjectBase, price: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="font-bold">₦{settings.singleSubjectBase.price.toLocaleString()}</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-gray-700">Free Lessons Limit</span>
                {editSettingsMode ? (
                  <input type="number" className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.freePreviews.lessons} onChange={e => setTempSettings({...tempSettings, freePreviews: {...tempSettings.freePreviews, lessons: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="font-bold">{settings.freePreviews.lessons}</span>
                )}
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-700">Free Quizzes Limit</span>
                {editSettingsMode ? (
                  <input type="number" className="w-20 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.freePreviews.quizzes} onChange={e => setTempSettings({...tempSettings, freePreviews: {...tempSettings.freePreviews, quizzes: parseInt(e.target.value) || 0}})} />
                ) : (
                  <span className="font-bold">{settings.freePreviews.quizzes}</span>
                )}
              </div>
            </div>
          </div>
        
        {/* Term-Based Pricing Settings */}
        <div className="bg-white rounded-2xl border border-gray-200 p-4 md:p-6">
            <h2 className="text-xl font-bold text-darkNavy mb-4">Term-Based Default Pricing</h2>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-700 font-medium">Single Subject Term Price (₦)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.termPricing?.singleSubject?.price || 0} onChange={e => setTempSettings({...tempSettings, termPricing: {...tempSettings.termPricing, singleSubject: {...tempSettings.termPricing?.singleSubject, price: parseInt(e.target.value) || 0}}})} />
                ) : (
                  <span className="font-bold">₦{(settings.termPricing?.singleSubject?.price || 2500).toLocaleString()}</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-gray-700 font-medium">3-Subject Bundle Term Price (₦)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.termPricing?.threeSubjects?.price || 0} onChange={e => setTempSettings({...tempSettings, termPricing: {...tempSettings.termPricing, threeSubjects: {...tempSettings.termPricing?.threeSubjects, price: parseInt(e.target.value) || 0}}})} />
                ) : (
                  <span className="font-bold">₦{(settings.termPricing?.threeSubjects?.price || 6500).toLocaleString()}</span>
                )}
              </div>
              <div className="flex justify-between items-center pt-3 border-t border-gray-100">
                <span className="text-gray-700 font-medium">5-Subject Bundle Term Price (₦)</span>
                {editSettingsMode ? (
                  <input type="number" className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2" value={tempSettings.termPricing?.sixSubjects?.price || 0} onChange={e => setTempSettings({...tempSettings, termPricing: {...tempSettings.termPricing, sixSubjects: {...tempSettings.termPricing?.sixSubjects, price: parseInt(e.target.value) || 0}}})} />
                ) : (
                  <span className="font-bold">₦{(settings.termPricing?.sixSubjects?.price || 10000).toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>
      </div>

      {/* Add Subject Modal */}
      <Modal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        title="Add New Subject"
        size="medium"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Subject Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={newSubject.subject}
              onChange={(e) => setNewSubject({ ...newSubject, subject: e.target.value })}
              placeholder="e.g., Mathematics"
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Monthly Price (₦)
              </label>
              <input
                type="number"
                value={newSubject.monthly}
                onChange={(e) => setNewSubject({ ...newSubject, monthly: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Termly Price (₦)
              </label>
              <input
                type="number"
                value={newSubject.termly}
                onChange={(e) => setNewSubject({ ...newSubject, termly: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Annual Price (₦)
              </label>
              <input
                type="number"
                value={newSubject.annual}
                onChange={(e) => setNewSubject({ ...newSubject, annual: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="active"
              checked={newSubject.active}
              onChange={(e) => setNewSubject({ ...newSubject, active: e.target.checked })}
              className="w-4 h-4 text-primary rounded focus:ring-2 focus:ring-primary/20"
            />
            <label htmlFor="active" className="text-sm font-medium text-gray-700">
              Active (visible to users)
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={() => setShowAddModal(false)}
              className="px-6 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAddSubject}
              className="px-6 py-2.5 bg-primary text-white rounded-xl font-semibold hover:bg-blue-700 transition-colors"
            >
              Add Subject
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Pricing;
